import type { FC } from 'react'
import { memo, useCallback, useMemo, useState } from 'react'
import { NavLink, useLocation, useParams } from 'react-router'

import classNames from 'classnames'
import { SidebarItem } from 'layout/Sidebar/SidebarItem/SidebarItem'
import { PATH_SEGMENT } from 'routes/pathSegments'

import { useScreenWidth } from 'app/hooks/useScreenWidth'
import { useAuth } from 'app/providers/auth/AuthProvider'

import { UtilService } from 'common/services/utilService'

import { PathUtils } from 'routes/routes.utils'
import { sidebarConfig } from './config'
import styles from './sidebar.module.scss'
import type { SidebarProps } from './sidebar.types'

export const Sidebar: FC<SidebarProps> = memo(
  ({ isSidebarVisible, toggleIsSidebarVisible, isChartsPage }) => {
    const [hoveredNavigation, setHoveredNavigation] = useState<string | null>(null)

    const location = useLocation()
    const { siteId } = useParams<{ siteId: string }>()

    const { isMobile } = useScreenWidth()
    const { user, isDoctor, isStaff, hasCompletedDocuments, hasEmrEnabled, hasProfile } = useAuth()

    const isUnconfirmedDoctor = useMemo(
      () => isDoctor && !hasCompletedDocuments,
      [isDoctor, hasCompletedDocuments],
    )

    const isSuperDoctor = useMemo(() => user?.is_super_doctor, [user])

    const isGuest = useMemo(() => !user, [user])

    const sidebarItems = useMemo(
      () =>
        sidebarConfig.getItems({
          isGuest,
          isUnconfirmedDoctor,
          isSuperDoctor,
          isStaff,
          hasEmrEnabled,
          hasProfile,
          userId: user?.id,
        }),
      [isGuest, isUnconfirmedDoctor, isSuperDoctor, isStaff, hasEmrEnabled, hasProfile, user?.id],
    )

    const handleChangeHoveredIcon = useCallback(
      (newHoveredIcon: string) => {
        if (hoveredNavigation !== newHoveredIcon) {
          setHoveredNavigation(newHoveredIcon)
        }
      },
      [hoveredNavigation],
    )

    const isActiveRoute = useCallback(
      (route: string) => {
        const pathSegments = location.pathname.split('/').map((segment) => segment.toLowerCase())
        return pathSegments[2].includes(route)
      },
      [location.pathname],
    )

    const sidebarItemClickHandler = useCallback(
      (e: React.MouseEvent) => {
        if (isGuest || isUnconfirmedDoctor) {
          e.preventDefault()
          return
        }
        if (isSidebarVisible && isMobile) {
          toggleIsSidebarVisible?.()
        }
      },
      [isGuest, isUnconfirmedDoctor, isSidebarVisible, isMobile, toggleIsSidebarVisible],
    )

    return (
      <div className={styles.sidebar} data-guest={!user} data-charts-page={isChartsPage}>
        <div className={styles.sidebarNavigation}>
          {sidebarItems.map(({ title, route, icon }, index: number) => {
            return (
              <NavLink
                key={index}
                data-cy={`sidebar-btn-${UtilService.replaceSpaceToUnderscore(title)}`}
                onClick={sidebarItemClickHandler}
                data-disabled={isGuest || isUnconfirmedDoctor}
                onMouseLeave={() => setHoveredNavigation(null)}
                onMouseOver={() => handleChangeHoveredIcon(title)}
                className={classNames(styles.navigationItem, styles.activeNavigationItem, {
                  [styles.activeAdminItem]: route === PATH_SEGMENT.ADMIN,
                  [styles.disabledRoute]:
                    ![PATH_SEGMENT.HOME, PATH_SEGMENT.FAQ, PATH_SEGMENT.SUPPORT].includes(route) &&
                    (isGuest || isUnconfirmedDoctor),
                })}
                to={PathUtils.getDynamicRoute(siteId, route)}>
                <SidebarItem
                  title={title}
                  icon={icon}
                  isActive={isActiveRoute(route)}
                  isHovered={hoveredNavigation === title}
                />
              </NavLink>
            )
          })}
        </div>
      </div>
    )
  },
)
